import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  ArrowRight, Settings, Plus, Edit3, Trash2, Loader2, 
  CheckCircle, XCircle, ToggleLeft, ToggleRight,
  FileText, CreditCard, Clock, Save, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import BreadcrumbNav from '../components/BreadcrumbNav';
import DashboardHeader from '../components/DashboardHeader';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InvoiceDropdownSettings() {
  const { platform } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { language } = useLanguage();
  
  const [dropdowns, setDropdowns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('service_types');
  
  // Add/Edit Modal
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({ name_ar: '', name_en: '' });
  const [saving, setSaving] = useState(false);
  
  // Delete confirmation
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const platformKey = platform === 'cloudgm-business' ? 'cloudgm_business' : 'cloudgm';
  const isCustomerPlatform = platform === 'cloudgm';

  const categories = [
    { 
      id: 'service_types', 
      nameAr: 'أنواع الخدمات', 
      nameEn: 'Service Types',
      icon: FileText,
      color: 'text-blue-400 bg-blue-500/20'
    },
    { 
      id: 'payment_methods', 
      nameAr: 'طرق الدفع', 
      nameEn: 'Payment Methods',
      icon: CreditCard,
      color: 'text-emerald-400 bg-emerald-500/20'
    },
    { 
      id: 'payment_statuses', 
      nameAr: 'حالات الدفع', 
      nameEn: 'Payment Statuses',
      icon: Clock,
      color: 'text-amber-400 bg-amber-500/20'
    }
  ];

  useEffect(() => {
    fetchDropdowns();
  }, [platform]);

  const fetchDropdowns = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/api/invoices/dropdowns/${platformKey}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setDropdowns(response.data.dropdowns);
      }
    } catch (error) {
      console.error('Error fetching dropdowns:', error);
      toast.error(language === 'ar' ? 'خطأ في جلب البيانات' : 'Error fetching data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async () => {
    if (!formData.name_ar || !formData.name_en) {
      toast.error(language === 'ar' ? 'يرجى ملء جميع الحقول' : 'Please fill all fields');
      return;
    }
    
    try {
      setSaving(true);
      const response = await axios.post(
        `${API}/api/invoices/dropdowns/${platformKey}/add-item`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: {
            category: activeCategory,
            name_ar: formData.name_ar,
            name_en: formData.name_en
          }
        }
      );
      
      if (response.data.success) {
        toast.success(language === 'ar' ? 'تمت الإضافة بنجاح' : 'Added successfully');
        fetchDropdowns();
        closeModal();
      }
    } catch (error) {
      console.error('Error adding item:', error);
      toast.error(language === 'ar' ? 'خطأ في الإضافة' : 'Error adding item');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleItem = async (itemId, currentStatus) => {
    try {
      const response = await axios.put(
        `${API}/api/invoices/dropdowns/${platformKey}/toggle-item/${activeCategory}/${itemId}`,
        null,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { is_active: !currentStatus }
        }
      );
      
      if (response.data.success) {
        toast.success(language === 'ar' ? 'تم التحديث' : 'Updated');
        fetchDropdowns();
      }
    } catch (error) {
      console.error('Error toggling item:', error);
      toast.error(language === 'ar' ? 'خطأ في التحديث' : 'Error updating');
    }
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete) return;
    
    try {
      const response = await axios.delete(
        `${API}/api/invoices/dropdowns/${platformKey}/delete-item/${activeCategory}/${itemToDelete.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success(language === 'ar' ? 'تم الحذف بنجاح' : 'Deleted successfully');
        fetchDropdowns();
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error(language === 'ar' ? 'خطأ في الحذف' : 'Error deleting');
    } finally {
      setDeleteDialog(false);
      setItemToDelete(null);
    }
  };

  const openAddModal = () => {
    setEditingItem(null);
    setFormData({ name_ar: '', name_en: '' });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setFormData({ name_ar: '', name_en: '' });
  };

  const getBreadcrumbItems = () => {
    const baseItems = [
      { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
      { label_ar: 'التحكم والمعلومات للمنصات', label_en: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
      { label_ar: 'CLOUDGM FZ LLC (للتحكم)', label_en: 'CLOUDGM FZ LLC (Control)', path: '/dashboard?view=platform-control&sub=fzlc' },
    ];

    if (isCustomerPlatform) {
      return [
        ...baseItems,
        { label_ar: 'التحكم والمعلومات (منصة العملاء)', label_en: 'Control & Info (Customers)', path: '/control/cloudgm' },
        { label_ar: 'التحكم بالقوائم المنسدلة للفواتير', label_en: 'Invoice Dropdown Settings', path: null }
      ];
    } else {
      return [
        ...baseItems,
        { label_ar: 'التحكم والمعلومات (منصة التجار)', label_en: 'Control & Info (Merchants)', path: '/control/cloudgm-business' },
        { label_ar: 'التحكم بالقوائم المنسدلة للفواتير', label_en: 'Invoice Dropdown Settings', path: null }
      ];
    }
  };

  const breadcrumbItems = getBreadcrumbItems();
  const currentCategoryInfo = categories.find(c => c.id === activeCategory);
  const currentItems = dropdowns?.[activeCategory] || [];

  return (
    <div className="min-h-screen bg-[#0a0a0a] py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Breadcrumb */}
        <div className="mb-4">
          <BreadcrumbNav items={breadcrumbItems} />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg bg-[#1a1a2e] border border-[#2a2a4e] hover:border-[#c9a227] transition-colors"
            >
              <ArrowRight className="h-5 w-5 text-[#c9a227]" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-white">
                {language === 'ar' ? 'إعدادات القوائم المنسدلة للفواتير' : 'Invoice Dropdown Settings'}
              </h1>
              <p className="text-sm text-[#808080]">
                {isCustomerPlatform 
                  ? (language === 'ar' ? 'منصة العملاء - CloudGM' : 'CloudGM Customer Platform')
                  : (language === 'ar' ? 'منصة التجار - CloudGM Business' : 'CloudGM Business Merchant Platform')
                }
              </p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-[#c9a227] animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Categories Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-[#1a1a2e] rounded-xl border border-[#2a2a4e] overflow-hidden">
                <div className="p-4 border-b border-[#2a2a4e]">
                  <h3 className="text-sm font-medium text-white">
                    {language === 'ar' ? 'الفئات' : 'Categories'}
                  </h3>
                </div>
                <div className="p-2">
                  {categories.map((category) => {
                    const Icon = category.icon;
                    const isActive = activeCategory === category.id;
                    const itemCount = dropdowns?.[category.id]?.length || 0;
                    
                    return (
                      <button
                        key={category.id}
                        onClick={() => setActiveCategory(category.id)}
                        className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors ${
                          isActive 
                            ? 'bg-[#c9a227]/20 text-[#c9a227]' 
                            : 'hover:bg-[#252540] text-[#808080] hover:text-white'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${category.color}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="flex-1 text-right">
                          <p className="text-sm font-medium">
                            {language === 'ar' ? category.nameAr : category.nameEn}
                          </p>
                          <p className="text-xs text-[#606060]">
                            {itemCount} {language === 'ar' ? 'عنصر' : 'items'}
                          </p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Items List */}
            <div className="lg:col-span-3">
              <div className="bg-[#1a1a2e] rounded-xl border border-[#2a2a4e] overflow-hidden">
                {/* Category Header */}
                <div className="p-4 border-b border-[#2a2a4e] flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {currentCategoryInfo && (
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${currentCategoryInfo.color}`}>
                        <currentCategoryInfo.icon className="w-5 h-5" />
                      </div>
                    )}
                    <div>
                      <h3 className="text-lg font-bold text-white">
                        {language === 'ar' ? currentCategoryInfo?.nameAr : currentCategoryInfo?.nameEn}
                      </h3>
                      <p className="text-xs text-[#808080]">
                        {currentItems.length} {language === 'ar' ? 'عنصر' : 'items'}
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={openAddModal}
                    className="bg-[#c9a227] hover:bg-[#b8922a] text-black"
                  >
                    <Plus className="w-4 h-4 ml-2" />
                    {language === 'ar' ? 'إضافة' : 'Add'}
                  </Button>
                </div>

                {/* Items */}
                {currentItems.length === 0 ? (
                  <div className="p-8 text-center">
                    <Settings className="w-12 h-12 text-[#404040] mx-auto mb-4" />
                    <p className="text-[#808080]">
                      {language === 'ar' ? 'لا توجد عناصر' : 'No items found'}
                    </p>
                  </div>
                ) : (
                  <div className="divide-y divide-[#2a2a4e]">
                    {currentItems.map((item) => (
                      <div 
                        key={item.id} 
                        className={`p-4 flex items-center justify-between hover:bg-[#252540] transition-colors ${
                          !item.is_active ? 'opacity-50' : ''
                        }`}
                      >
                        <div className="flex-1">
                          <p className="text-white font-medium">{item.name_ar}</p>
                          <p className="text-sm text-[#808080]">{item.name_en}</p>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {/* Toggle Active */}
                          <button
                            onClick={() => handleToggleItem(item.id, item.is_active)}
                            className={`p-2 rounded-lg transition-colors ${
                              item.is_active 
                                ? 'bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30' 
                                : 'bg-[#2a2a4e] text-[#606060] hover:bg-[#3a3a5e]'
                            }`}
                            title={item.is_active 
                              ? (language === 'ar' ? 'تعطيل' : 'Disable')
                              : (language === 'ar' ? 'تفعيل' : 'Enable')
                            }
                          >
                            {item.is_active ? (
                              <ToggleRight className="w-5 h-5" />
                            ) : (
                              <ToggleLeft className="w-5 h-5" />
                            )}
                          </button>
                          
                          {/* Delete */}
                          <button
                            onClick={() => {
                              setItemToDelete(item);
                              setDeleteDialog(true);
                            }}
                            className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                            title={language === 'ar' ? 'حذف' : 'Delete'}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Add Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e]">
            <DialogHeader>
              <DialogTitle className="text-white">
                {language === 'ar' ? 'إضافة عنصر جديد' : 'Add New Item'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm text-[#808080] mb-1">
                  {language === 'ar' ? 'الاسم بالعربي *' : 'Arabic Name *'}
                </label>
                <Input
                  value={formData.name_ar}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_ar: e.target.value }))}
                  className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
                  placeholder={language === 'ar' ? 'أدخل الاسم بالعربي' : 'Enter Arabic name'}
                />
              </div>
              
              <div>
                <label className="block text-sm text-[#808080] mb-1">
                  {language === 'ar' ? 'الاسم بالإنجليزي *' : 'English Name *'}
                </label>
                <Input
                  value={formData.name_en}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_en: e.target.value }))}
                  className="bg-[#0a0a0a] border-[#2a2a4e] text-white"
                  placeholder={language === 'ar' ? 'أدخل الاسم بالإنجليزي' : 'Enter English name'}
                />
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button
                onClick={closeModal}
                className="bg-[#2a2a4e] hover:bg-[#3a3a5e] text-white"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={handleAddItem}
                disabled={saving}
                className="bg-[#c9a227] hover:bg-[#b8922a] text-black"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Plus className="w-4 h-4 ml-2" />
                    {language === 'ar' ? 'إضافة' : 'Add'}
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
          <DialogContent className="bg-[#1a1a2e] border-[#2a2a4e]">
            <DialogHeader>
              <DialogTitle className="text-white">
                {language === 'ar' ? 'تأكيد الحذف' : 'Confirm Delete'}
              </DialogTitle>
            </DialogHeader>
            <p className="text-[#808080]">
              {language === 'ar' 
                ? `هل أنت متأكد من حذف "${itemToDelete?.name_ar}"؟`
                : `Are you sure you want to delete "${itemToDelete?.name_en}"?`
              }
            </p>
            <DialogFooter className="gap-2">
              <Button
                onClick={() => setDeleteDialog(false)}
                className="bg-[#2a2a4e] hover:bg-[#3a3a5e] text-white"
              >
                {language === 'ar' ? 'إلغاء' : 'Cancel'}
              </Button>
              <Button
                onClick={handleDeleteItem}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                {language === 'ar' ? 'حذف' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
